// src/Modal.tsx

import React, { useEffect, useRef } from 'react';
import { X } from 'lucide-react';

interface ModalProps {
  isOpen: boolean;
  onClose: () => void;
  title: string;
  children: React.ReactNode;
}

const Modal: React.FC<ModalProps> = ({ isOpen, onClose, title, children }) => {
  // We use a ref to manage clicks outside the modal content
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // Handle keyboard navigation and scroll lock
    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscape);
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = 'unset';
    };
  }, [isOpen, onClose]);

  // Close modal when clicking the backdrop
  const handleBackdropClick = (event: React.MouseEvent) => {
    if (event.target === event.currentTarget) {
      onClose();
    }
  };

  if (!isOpen) return null;

  return (
    <div
      // The backdrop uses a dark gradient with blur for depth
      className="fixed inset-0 bg-[#050614]/80 flex items-center justify-center p-4 z-50 backdrop-blur-sm"
      onClick={handleBackdropClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="modal-title"
    >
      <div
        ref={modalRef}
        // The modal container uses a subtle gradient background
        className="bg-gradient-to-br from-[#0a1425] to-[#050614] rounded-xl 
                 border border-white/10 shadow-2xl shadow-black/50
                 w-full max-w-3xl max-h-[90vh] overflow-hidden flex flex-col"
      >
        {/* Header section with refined styling */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 bg-[#0a1425]">
          <h2 id="modal-title" className="text-xl font-semibold text-white">
            {title}
          </h2>
          <button
            onClick={onClose}
            className="text-white/60 hover:text-white transition-colors p-2
                     hover:bg-white/5 rounded-lg"
            aria-label="Close modal"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content area with improved contrast and readability */}
        <div className="overflow-y-auto flex-1 bg-gradient-to-b from-[#0a1425]/50 to-[#050614]/50">
          <div className="p-6">
            {/* Add a wrapper to ensure consistent text styling */}
            <div className="text-white/90">
              {children}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Modal;